/**
 * /reset-password — consume a reset token, set a new password.
 *
 * Reads `?token=` from the URL. On success the backend returns a
 * fresh JWT — we store it via AuthContext.login-equivalent path
 * (set token + fetch /me) and route the user to /.
 *
 * If the token is missing from the URL we show an error immediately
 * rather than waiting for the form submit.
 */

import { Alert, Stack } from '@mui/material';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { resetPasswordRequest } from '../../api/auth';
import { AUTH_TOKEN_KEY } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { AuthLayout } from './AuthLayout';
import { AuthPrimaryButton } from './AuthPrimaryButton';
import { AuthTextField } from './AuthTextField';
import { describeError } from './errorMessages';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const { refreshUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && password !== confirm;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setError(null);
    setSubmitting(true);
    try {
      const tokenResp = await resetPasswordRequest({ token, new_password: password });
      localStorage.setItem(AUTH_TOKEN_KEY, tokenResp.access_token);
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout
        title="Invalid reset link"
        footer={
          <>
            <Link to="/forgot-password">Request a new reset link</Link>
          </>
        }
      >
        <Alert severity="error">
          This page expects a reset token. Click the link from the password-reset email — opening
          this page directly won't work.
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Pick something you'll remember — at least 8 characters."
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <AuthTextField
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            autoFocus
          />

          <AuthTextField
            label="Confirm password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            error={mismatch}
            helperText={mismatch ? 'Passwords do not match.' : ' '}
          />

          <AuthPrimaryButton
            type="submit"
            disabled={password.length < 8 || password !== confirm}
            loading={submitting}
          >
            Set password
          </AuthPrimaryButton>
        </Stack>
      </form>
    </AuthLayout>
  );
}
